import { apiService } from './api';

export interface ChatMessageDTO {
  id: string;
  assemblyId: string;
  userId?: string;
  userName: string;
  userRole?: string;
  message: string;
  type: string;
  timestamp: Date;
  replyTo?: string;
  isModerated: boolean;
  isDeleted: boolean;
}

export interface GetMessagesResponse {
  success: boolean;
  data: ChatMessageDTO[];
  total: number;
  limit: number;
  offset: number;
}

export interface GetRecentMessagesResponse {
  success: boolean;
  data: ChatMessageDTO[];
  count: number;
}

export interface MessageStatsResponse {
  success: boolean;
  data: {
    total: number;
    byType: Record<string, number>;
    moderated: number;
    deleted: number;
  };
}

class ChatService {
  /**
   * Obtener todos los mensajes de una asamblea con paginación
   */
  async getAssemblyMessages(
    assemblyId: string,
    options?: {
      limit?: number;
      offset?: number;
    }
  ): Promise<ChatMessageDTO[]> {
    try {
      const params = new URLSearchParams();
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.offset) params.append('offset', options.offset.toString());

      const response = await apiService.api.get<GetMessagesResponse>(
        `/assemblies/${assemblyId}/messages?${params.toString()}`
      );

      if (response.data.success) {
        // Convertir timestamps a Date objects
        return response.data.data.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        }));
      }

      throw new Error('Error al obtener mensajes');
    } catch (error: any) {
      console.error('Error fetching assembly messages:', error);
      throw error;
    }
  }

  /**
   * Obtener mensajes recientes de una asamblea (últimos N)
   */
  async getRecentMessages(assemblyId: string, limit: number = 100): Promise<ChatMessageDTO[]> {
    try {
      const response = await apiService.api.get<GetRecentMessagesResponse>(
        `/assemblies/${assemblyId}/messages/recent?limit=${limit}`
      );

      if (response.data.success) {
        // Convertir timestamps a Date objects
        return response.data.data.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        }));
      }

      throw new Error('Error al obtener mensajes recientes');
    } catch (error: any) {
      console.error('Error fetching recent messages:', error);
      throw error;
    }
  }

  /**
   * Eliminar un mensaje (solo admin/manager)
   */
  async deleteMessage(messageId: string): Promise<void> {
    try {
      const response = await apiService.api.delete(
        `/messages/${messageId}`
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al eliminar mensaje');
      }
    } catch (error: any) {
      console.error('Error deleting message:', error);
      throw error;
    }
  }

  /**
   * Moderar un mensaje (solo admin/manager)
   */
  async moderateMessage(messageId: string): Promise<void> {
    try {
      const response = await apiService.api.put(
        `/messages/${messageId}/moderate`,
        {}
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al moderar mensaje');
      }
    } catch (error: any) {
      console.error('Error moderating message:', error);
      throw error;
    }
  }

  /**
   * Obtener estadísticas de mensajes de una asamblea
   */
  async getMessageStats(assemblyId: string): Promise<MessageStatsResponse['data']> {
    try {
      const response = await apiService.api.get<MessageStatsResponse>(
        `/assemblies/${assemblyId}/messages/stats`
      );

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error('Error al obtener estadísticas');
    } catch (error: any) {
      console.error('Error fetching message stats:', error);
      throw error;
    }
  }
}

export const chatService = new ChatService();
